/*
 * SmartReserve vier-batterijenmodel (okt 2026 · "VB-2026.10")
 * Rekent dezelfde huishoudsituatie door voor elke batterij in de line-up (matchtest, bespaarcalculator,
 * blok "In jouw huis" op de productpagina en de vergelijkingspagina).
 *
 * Bouwt op assets/sr-batterijmodel.js: hetzelfde maandprofiel, dezelfde tarieven, slijtage, benutting
 * en dynamisch-contractaanpak. Wat hier per batterij verschilt, komt uit de productgegevens:
 *  - opstellingen en prijzen uit de varianten (variant-metafields kwh, vermogen_w, units);
 *  - bruikbaar = nominaal × ontlaaddiepte (product-metafield);
 *  - vermogen: altijd de variant-vermogen_w (eigen groep, maximaal vermogen; besluit Kelvin 6-10-2026);
 *  - noodstroom_w en pv_dc_ingang voor de harde eisen.
 * Aannames (rendement, ontlaaduren, grensbedragen, verbruik per gezin enz.) komen uit de thema-instellingen
 * (window.SR_VIER.aannames); de waarden hieronder zijn alleen een terugval.
 *
 * Rekenregels (zie de opdracht "vier batterijen"):
 *  - Opslagbehoefte per dag = kleinste van (zonoverschot per dag, minimaal 3 kWh) en 60% van het dagverbruik.
 *  - Opstelling = de goedkoopste die je bij ons kunt kopen en waarvan het bruikbare deel de behoefte dekt.
 *    Bij veel vermogen: bij dezelfde kWh de variant met het hoogste vermogen (Anker 2 units, Halo 2 torens).
 *    Past niets: de grootste, gelabeld "Minder geschikt".
 *  - Per dag ontlaadt een batterij hooguit vermogen × ontlaaduren (standaard 8 uur, avond en nacht).
 *  - Rangschikking (besluit Kelvin 6-10-2026, avond: "het moet echt passen bij degene die de test doet, alleen moet
 *    de HYXI altijd als een goede oplossing ertussen staan"): eerst harde eisen, dan een pasbaarheidsscore.
 *    Die telt op: (1) hoe goed het karakter van de batterij past bij de antwoorden (woning, grote verbruikers,
 *    wens, behoefte per dag), zie fitFor(), en (2) wat hij in jouw huis oplevert per geïnvesteerde euro.
 *    Liggen twee batterijen vrijwel gelijk, dan wint de allrounder.
 *  - De allrounder (HYXI Halo) staat altijd bij de eerste twee, met het label "Ook goed" als hij niet de beste match is.
 */
(function (root) {
  'use strict';
  var VERSION = 'VB-2026.10.2';
  var M = root.SRBatterijModel || (typeof require === 'function' ? require('./sr-batterijmodel.js') : null);

  var DEFAULTS = {
    eta: 0.90,              // rendement heen en terug, voor alle vier gelijk
    dischargeHours: 8,      // max ontlaaduren per dag op vol vermogen (avond en nacht, gelijk aan het Batterijcheck-model)
    chargeHoursLow: 5, chargeHoursHigh: 6,
    standbyW: 9,            // stand-by per unit of toren
    minStoreKwh: 3,         // ondergrens zonoverschot in de behoefteformule
    needShare: 0.60,        // deel van het dagverbruik dat de batterij maximaal hoeft te dekken
    tieMargin: 0.05,        // binnen 5% wint de allrounder
    okFraction: 0.60,       // "Ook goed" vanaf 60% van de score van de beste match
    oversizeFactor: 1.5,    // kleinste opstelling is ruim groter dan de behoefte
    priceWeight: 0.5,       // gewicht lagere prijs bij "zo voordelig mogelijk" en "klein beginnen"
    noodstroomMinW: 3000,   // noodstroom met grote verbruikers vraagt minstens dit vermogen
    maxTowers: 3,           // combinaties van torens (alleen bij de combinatiebatterij)
    grensMidden: 2500, grensHoog: 3500,
    feedCostGunstig: 0.10,  // gunstig scenario: vermeden terugleverkosten per kWh als de bezoeker zelf niets invult (besluit Kelvin 6-10-2026)
    allrounder: 'huisvriend', // staat altijd bij de eerste twee (besluit Kelvin 6-10-2026)
    krachtKey: 'krachtpatser', meeKey: 'meeverhuizer', rekenKey: 'rekenmeester',
    geldGewicht: 2.5,       // punten voor opbrengst per euro (de beste krijgt er 2,5, verlies telt als 0), naast de pasbaarheid
    gelijkPunten: 0.25,     // binnen zoveel punten van elkaar wint de allrounder
    ookGoedPunten: 2.5,     // "Ook goed" als de score hooguit zoveel punten onder de beste match ligt
    combineKey: 'huisvriend', combineName: 'Halo'
  };

  function A(opts) {
    var a = {}, k, src = (opts && opts.aannames) || (root.SR_VIER && root.SR_VIER.aannames) || {};
    for (k in DEFAULTS) a[k] = DEFAULTS[k];
    for (k in src) if (src[k] !== null && src[k] !== '' && src[k] !== undefined) a[k] = src[k];
    return a;
  }

  /* ---------- Opstellingen per batterij ---------- */
  function setupsFor(bat, a) {
    // niet meer units dan de stapelinstelling toestaat (bijvoorbeeld Anker maximaal 2, besluit Kelvin 6-10-2026)
    var maxU = bat.stapel ? Math.max(bat.stapel.towersMax || 1, bat.stapel.towersMaxBasis || 0) : 99;
    var base = (bat.variants || []).filter(function (v) { return v.kwh > 0 && v.units > 0 && v.units <= maxU && v.price > 0 && v.available !== false; })
      .map(function (v) {
        return { key: String(v.id), title: v.title, kwh: v.kwh, w: v.w || 0, units: v.units, modules: v.modules || 1,
                 price: v.price, parts: [{ id: v.id, qty: 1, kwh: v.kwh }], variantId: v.id, image: v.image || '' };
      });
    if (bat.key !== a.combineKey || !base.length) return base;
    // Torens combineren (tot maxTowers), zoals de matchtest al deed: elke toren een eigen omvormer
    var out = base.slice();
    var maxT = Math.max(1, Math.min(a.maxTowers, bat.maxUnits || a.maxTowers));
    function rec(start, chosen) {
      if (chosen.length >= 2) {
        var kwh = 0, w = 0, price = 0, counts = {};
        chosen.forEach(function (s) { kwh += s.kwh; w += s.w; price += s.price; counts[s.key] = (counts[s.key] || 0) + 1; });
        var parts = Object.keys(counts).map(function (k) { var s = base.filter(function (b) { return b.key === k; })[0]; return { id: s.variantId, qty: counts[k], kwh: s.kwh }; })
          .sort(function (x, y) { return y.kwh - x.kwh; });
        out.push({ key: parts.map(function (p) { return p.id + 'x' + p.qty; }).join('+'), title: comboTitle(parts, a), kwh: round2(kwh), w: w,
                   units: chosen.length, modules: 0, price: price, parts: parts, variantId: parts[0].id, image: chosen[0].image });
      }
      if (chosen.length === maxT) return;
      for (var i = start; i < base.length; i++) rec(i, chosen.concat([base[i]]));
    }
    rec(0, []);
    return out;
  }
  /* Opstellingen tot het maximum (stapelcomponent) die geen variant zijn: op aanvraag, nooit met een prijs.
     bat.stapel: { towersMax, towersMaxBasis, minModules, maxModules, top, module, unit } uit sr-stapel-config. */
  function aanvraagSetups(bat, online) {
    var st = bat.stapel, out = [];
    if (!st || !bat.stapKwh) return out;
    for (var n = 1; n <= Math.max(st.towersMax, st.towersMaxBasis || 0); n++) {
      for (var m = st.minModules; m <= st.maxModules; m++) {
        if (n > st.towersMax && m > 1) continue;
        var kwh = round2(n * m * bat.stapKwh);
        var exists = online.some(function (s) { return s.units === n && Math.abs(s.kwh - kwh) < 0.05 && (s.parts.length === 1 || s.parts.every(function (p) { return p.qty === n; })); });
        if (exists) continue;
        out.push({ key: 'req-' + n + '-' + m, title: stapelTitle(bat, n, m), kwh: kwh, w: (bat.groepW || 0) * n, units: n, modules: n * m,
                   price: null, parts: [], variantId: null, image: '', onRequest: true, n: n, m: m });
      }
    }
    return out;
  }
  function stapelTitle(bat, n, m) {
    var st = bat.stapel || {}, kwh = (Math.round(n * m * bat.stapKwh * 10) / 10).toLocaleString('nl-NL') + ' kWh';
    var unit = n > 1 ? n + ' ' + (st.unitPlural || 'units') : '';
    var extra = m - (st.topCounts === false ? 0 : 1);
    var inner = st.module && extra > 0 ? (st.top || '') + ' met ' + extra + ' ' + st.module : (st.top || '');
    if (n > 1) return kwh + ', ' + unit + (inner ? ' (elk ' + inner + ')' : '');
    return kwh + (inner ? ', ' + inner : '');
  }
  function comboTitle(parts, a) {
    return parts.map(function (p) { return (p.qty > 1 ? p.qty + '× ' : '') + a.combineName + ' ' + fmtNum(p.kwh); }).join(' + ');
  }
  function round2(n) { return Math.round(n * 100) / 100; }
  function fmtNum(n) { return (Math.round(n * 100) / 100).toLocaleString('nl-NL', { maximumFractionDigits: 2 }); }

  /* ---------- Invoer ---------- */
  // raw = model-invoer van SRBatterijModel (usageKwh, usageType, panels, pvKwh, exportKwh, profile, contract, buy, sell, feedCost)
  //       plus: groep ('ja'|'nee'|'onbekend'), wens ('voordelig'|'noodstroom'|'maximaal'|'klein'|''), groot [..],
  //       woning ('appartement'|'rijtjeshuis'|'twee_onder_een_kap'|'vrijstaand'|'')
  function prepare(raw) {
    var n = M.normalise(raw);
    var inp = n.input;
    var hh = M.household(inp);
    var directYear = 0, nightYear = 0;
    hh.months.forEach(function (x) { directYear += x.direct * x.days; nightYear += x.nightNeed * x.days; });
    return { raw: raw || {}, inp: inp, hh: hh, warnings: n.warnings, directYear: directYear, nightYear: nightYear };
  }

  function needPerDay(ctx, a) {
    var dailySurplus = ctx.hh.surplusYear / 365;
    var dailyUse = ctx.hh.totalUse / 365;
    return Math.min(Math.max(a.minStoreKwh, dailySurplus), a.needShare * dailyUse);
  }

  function powerHeavy(raw) {
    var g = raw.groot || [];
    var has = function (k) { return g.indexOf(k) !== -1; };
    return raw.groep !== 'nee' && ((raw.wens === 'noodstroom' && g.filter(function (k) { return k !== 'geen'; }).length > 0) || (has('wp') && has('ev')));
  }

  /* ---------- Eén scenario voor één opstelling (zelfde opbouw als SRBatterijModel.scenario) ---------- */
  function scenario(ctx, bat, setup, wattEff, sc, a) {
    var inp = ctx.inp, hh = ctx.hh, t = M.TARIFFS[inp.contract], C = M.CONSTANTS;
    var eta = a.eta;
    var util = sc === 'low' ? C.UTIL_LOW : C.UTIL_HIGH;
    var cap = setup.kwh * bat.dod * C.DEGRADATION;
    var kw = wattEff / 1000;
    var chargeLim = kw * (sc === 'low' ? a.chargeHoursLow : a.chargeHoursHigh);
    var dischargeLim = kw * a.dischargeHours;
    // Gunstig scenario: terugleverkosten zoals veel leveranciers die rekenen, tenzij de bezoeker een eigen bedrag invult
    var feedCost = inp.feedCost > 0 ? inp.feedCost : (sc === 'high' ? (a.feedCostGunstig || 0) : 0);
    var sellNet = inp.sell - feedCost;
    var solarStored = 0, solarDelivered = 0, gridDelivered = 0, solarValue = 0, dynValue = 0;
    var dynDays = sc === 'low' ? C.DYN_DAYS_LOW : C.DYN_DAYS_HIGH;
    var spread = t.spreadLow != null ? (sc === 'low' ? t.spreadLow : t.spreadHigh) : 0;
    for (var m = 0; m < 12; m++) {
      var x = hh.months[m];
      var need = Math.min(x.nightNeed, dischargeLim);
      var stored = Math.min(x.surplus, cap, need / eta, chargeLim) * util;
      var delivered = stored * eta;
      solarStored += stored * x.days;
      solarDelivered += delivered * x.days;
      solarValue += x.days * (delivered * inp.buy - stored * sellNet);
      if (inp.contract === 'dynamisch') {
        var spare = Math.max(0, Math.min(cap, need / eta) - stored);
        var dDays = x.days * dynDays / 365;
        gridDelivered += spare * eta * dDays;
        dynValue += dDays * spare * (eta * (t.low + spread) - t.low);
      }
    }
    var standbyCost = setup.units * a.standbyW * 8.76 * inp.buy;
    return { yearly: solarValue + Math.max(0, dynValue) - standbyCost, solarStored: solarStored, solarDelivered: solarDelivered,
             gridDelivered: gridDelivered, standbyCost: standbyCost };
  }

  function evaluate(ctx, bat, setup, wattEff, a) {
    var C = M.CONSTANTS;
    var lo = scenario(ctx, bat, setup, wattEff, 'low', a);
    var hi = scenario(ctx, bat, setup, wattEff, 'high', a);
    var mid = (lo.yearly + hi.yearly) / 2;
    var inp = ctx.inp, use = ctx.hh.totalUse;
    var importNow = Math.max(0, use - ctx.directYear);
    var costNowYear = importNow * inp.buy - ctx.hh.surplusYear * (inp.sell - inp.feedCost);
    var delivered = (lo.solarDelivered + hi.solarDelivered + lo.gridDelivered + hi.gridDelivered) / 2;
    return {
      yearlyLow: lo.yearly, yearlyHigh: hi.yearly, yearlyMid: mid,
      paybackFast: hi.yearly > 0 ? setup.price / hi.yearly : null,
      paybackSlow: lo.yearly > 0 ? setup.price / lo.yearly : null,
      net10Low: lo.yearly * C.HORIZON - setup.price, net10High: hi.yearly * C.HORIZON - setup.price,
      net10Mid: mid * C.HORIZON - setup.price,
      surplusPct: ctx.hh.surplusYear > 0 ? Math.min(1, ((lo.solarStored + hi.solarStored) / 2) / ctx.hh.surplusYear) : 0,
      nightPct: ctx.nightYear > 0 ? Math.min(1, delivered / ctx.nightYear) : 0,
      kwhPrijsNu: use > 0 ? costNowYear / use : 0,
      kwhPrijsMet: use > 0 ? (costNowYear - hi.yearly + setup.price / C.HORIZON) / use : 0
    };
  }

  /* ---------- Keuze van de opstelling per batterij ---------- */
  function chooseSetup(bat, setups, need, heavy, req, klein) {
    if (klein) {
      // "Klein beginnen" (besluit Kelvin 6-10-2026): de instapopstelling die je online kunt kopen; later uitbreiden
      return { setup: setups.slice().sort(function (x, y) { return x.price - y.price || x.kwh - y.kwh; })[0], fits: true, klein: true };
    }
    var cover = setups.filter(function (s) { return s.kwh * bat.dod >= need - 1e-9; });
    var fits = cover.length > 0;
    var pick;
    if (fits) {
      pick = cover.slice().sort(function (x, y) { return x.price - y.price || x.units - y.units; })[0];
    } else {
      var reqCover = (req || []).filter(function (s) { return s.kwh * bat.dod >= need - 1e-9; })
        .sort(function (x, y) { return x.kwh - y.kwh || x.units - y.units; });
      if (reqCover.length) return { setup: reqCover[0], fits: true, onRequest: true };
      var all = setups.concat(req || []);
      pick = all.slice().sort(function (x, y) { return y.kwh - x.kwh || (x.price || 0) - (y.price || 0); })[0];
      if (pick.onRequest) return { setup: pick, fits: false, onRequest: true };
    }
    if (heavy && pick) {
      // zelfde kWh, meer vermogen: de goedkoopste opstelling met een hoger vermogen (Anker 2 units, Halo 2 torens)
      var more = setups.filter(function (s) { return Math.abs(s.kwh - pick.kwh) < 1e-6 && s.w > pick.w; });
      more.sort(function (x, y) { return x.price - y.price || x.units - y.units; });
      if (more[0]) pick = more[0];
    }
    return { setup: pick, fits: fits };
  }

  function bestFor(ctx, bat, a, forcedKey) {
    var setups = setupsFor(bat, a);
    if (!setups.length) return null;
    var req = aanvraagSetups(bat, setups);
    var need = needPerDay(ctx, a);
    var heavy = powerHeavy(ctx.raw);
    var klein = ctx.raw.wens === 'klein' && !forcedKey;
    var ch = chooseSetup(bat, setups, need, heavy, req, klein);
    var setup = ch.setup, reqSetup = null;
    if (forcedKey) { setups.concat(req).forEach(function (s) { if (s.key === forcedKey) setup = s; }); }
    if (setup.onRequest) {
      // Doorrekenen met de grootste opstelling die je online kunt kopen (met die uitleg erbij)
      reqSetup = setup;
      setup = setups.slice().sort(function (x, y) { return y.kwh - x.kwh || x.price - y.price; })[0];
    }
    // Besluit Kelvin 6-10-2026: matchtest en calculator gaan altijd uit van een eigen groep en het maximale vermogen.
    var groep = ctx.raw.groep === 'nee' ? 'nee' : 'ja';
    var stop = bat.stopW || 800;
    var wattEff = groep === 'nee' ? stop : (setup.w || stop);
    var ev = evaluate(ctx, bat, setup, wattEff, a);
    var extraGroep = null;
    if (groep === 'nee' && setup.w > stop) {
      var evG = evaluate(ctx, bat, setup, setup.w, a);
      extraGroep = { w: setup.w, yearlyLow: evG.yearlyLow - ev.yearlyLow, yearlyHigh: evG.yearlyHigh - ev.yearlyHigh };
    }
    var usable = setup.kwh * bat.dod;
    var maxDaily = wattEff / 1000 * a.dischargeHours;
    return {
      key: bat.key, bat: bat, setup: setup, setups: setups, aanvraag: req, reqSetup: reqSetup,
      fits: ch.klein ? true : (reqSetup ? (reqSetup.kwh * bat.dod >= need - 1e-9) : usable >= need - 1e-9), klein: !!ch.klein,
      need: need, usable: usable, wattEff: wattEff, maxDaily: maxDaily,
      powerLimited: usable > maxDaily + 0.05 && need > maxDaily + 0.05, oversize: !ch.klein && usable > need * a.oversizeFactor, extraGroep: extraGroep, ev: ev,
      price: setup.price, grote: (reqSetup || setup).units > 1 || setup.price >= a.grensHoog
    };
  }

  /* ---------- Harde eisen, pasbaarheid, labels ---------- */
  function hardFail(ctx, r, a) {
    var raw = ctx.raw, g = (raw.groot || []).filter(function (k) { return k !== 'geen'; });
    if (!r.fits) return 'groot';
    if (raw.wens === 'noodstroom' && g.length && (r.bat.noodW || 0) < a.noodstroomMinW) return 'nood';
    return '';
  }

  function eur(n) { return '€ ' + Math.round(n).toLocaleString('nl-NL'); }
  function kwhTxt(n) { return (Math.round(n * 10) / 10).toLocaleString('nl-NL', { maximumFractionDigits: 1 }) + ' kWh'; }
  function wTxt(n) { return Math.round(n).toLocaleString('nl-NL') + ' W'; }
  // kWh zoals in de varianttitel ("5,12 kWh", "2,7 kWh"), anders afgerond
  function setupKwh(r) { var t = String(r.setup.title || '').replace(/^Totaal\s*/i, ''), m = t.match(/^[\d.,]+\s*kWh/); return m ? m[0] : kwhTxt(r.setup.kwh); }
  function stapTxt(n) { var two = Math.abs(n * 100 - Math.round(n * 10) * 10) >= 1.5; return n.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: two ? 2 : 1 }) + ' kWh'; }

  /* Antwoorden in vlaggen, één keer per berekening */
  function profiel(ctx, need, rs) {
    var raw = ctx.raw, g = (raw.groot || []).filter(function (k) { return k !== 'geen'; });
    var has = function (k) { return g.indexOf(k) !== -1; };
    var won = raw.woning || '', w = raw.wens || '';
    return { wp: has('wp'), ev: has('ev'), airco: has('airco'), groot: g, won: won, wens: w, need: need,
             app: won === 'appartement', gezin: won === 'rijtjeshuis' || won === 'twee_onder_een_kap' || won === 'vrijstaand',
             ruim: won === 'vrijstaand' || won === 'twee_onder_een_kap',
             maxGroepW: Math.max.apply(null, rs.map(function (r) { return r.bat.groepW || 0; })),
             maxNoodW: Math.max.apply(null, rs.map(function (r) { return r.bat.noodW || 0; })) };
  }

  /*
   * Pasbaarheid: hoe goed het karakter van de batterij past bij de antwoorden. Geeft punten plus de belangrijkste
   * reden (voor de uitleg). Gebaseerd op de klasse van elke batterij (metafields klasse, voor_wie, tagline):
   *  - De Krachtpatser: warmtepomp, elektrische auto, airco, noodstroom met grote verbruikers, ruim huis.
   *  - De Meeverhuizer: appartement of huurhuis, klein beginnen, kleine behoefte; licht en mee te nemen.
   *  - De Rekenmeester: zo voordelig mogelijk, rijtjeshuis, gewone behoefte zonder zware verbruikers.
   *  - De Huisvriend: de allrounder voor een gezinshuis, noodstroom en maximaal besparen, groter dan 8 kWh per dag.
   */
  function fitFor(r, P, a) {
    var pts = 0, why = '';
    function add(n, reden) { pts += n; if (reden && n > 0 && !why) why = reden; }
    // Wat iemand zelf het belangrijkst vindt, weegt het zwaarst: bij "zo voordelig mogelijk" de laagste prijs die past
    if (P.wens === 'voordelig' && r.isCheapest) add(3.5, 'voordelig');
    if (r.key === a.krachtKey) {
      if (P.wp && P.ev) add(3.5, 'wpev'); else if (P.wp) add(2, 'wp'); else if (P.ev) add(2, 'ev');
      if (P.wens === 'noodstroom' && P.groot.length) add(2, 'nood');
      if (P.airco) add(0.5, 'airco');
      if (P.wens === 'maximaal' && P.need >= 7) add(1, 'max');
      if (P.ruim && (P.wp || P.ev)) add(1, '');
      if (P.app) add(-3);
      if (P.wens === 'klein') add(-2);
      if (P.wens === 'voordelig') add(-1);
    } else if (r.key === a.meeKey) {
      if (P.app) add(3, 'app');
      if (P.wens === 'klein') add(3, 'klein');
      if (P.need <= 4.8 && !P.wp && !P.ev) add(1, 'klein');
      if (P.wp || P.ev) add(-3);
      if (P.wens === 'noodstroom') add(-1);
      if (P.need > 9) add(-1.5);
    } else if (r.key === a.rekenKey) {
      if (P.wens === 'voordelig') add(1, 'voordelig');
      if (P.won === 'rijtjeshuis') add(1, 'rijtjes');
      if (P.wens === 'klein') add(1, '');
      if (!(P.wp && P.ev) && P.need >= 2.99 && P.need <= 10) add(0.5, '');
      if (P.wp && P.ev) add(-1.5);
      if (P.wens === 'noodstroom') add(-0.5);
      if (P.app) add(-1);
    } else if (r.key === a.allrounder) {
      add(1.5, 'allround');
      if (P.wens === 'maximaal') { pts += 1; why = 'max'; }
      if (P.wens === 'noodstroom') { pts += 1; why = 'nood'; }
      if (P.gezin) pts += 0.5;
      if (P.need >= 8) pts += 0.5;
    }
    return { pts: pts, why: why };
  }

  function geldZin(r) {
    var pbFast = r.ev.paybackFast && r.ev.paybackFast <= 20 ? (Math.round(r.ev.paybackFast * 10) / 10).toString().replace('.', ',') : '';
    if (r.ev.net10High > 0) return ' In jouw huis levert hij ongeveer ' + eur(r.ev.net10High) + ' winst op over 10 jaar.';
    if (pbFast) return ' Je hebt hem in ongeveer ' + pbFast + ' jaar terugverdiend.';
    return ' Met deze antwoorden is het vooral een keuze voor gemak en noodstroom.';
  }

  function uitlegFor(r, label, ctx, a, best, tieHalo, P) {
    var stopW = (r.bat.stopW || 800).toLocaleString('nl-NL');
    var aanvraagZin = r.reqSetup && r.fits ? ' Past het best: ' + r.reqSetup.title + ', prijs op aanvraag. De besparing hieronder is gerekend met de grootste opstelling die je online kunt kopen (' + r.setup.title + ').' : '';
    var u = uitlegBase(r, label, ctx, a, best, tieHalo, stopW, P);
    return r.fail || label === 'Minder geschikt' ? u : u + aanvraagZin;
  }
  function beste(r, ctx, a, tieHalo, P) {
    if (r.fail === 'groot') return 'Ook de grootste opstelling dekt je behoefte niet helemaal. Wat er wel kan, bekijken we graag met je via maatwerkadvies.';
    if (tieHalo) return 'Scoort in jouw huis bijna gelijk met ' + tieHalo + '. Dan kiezen wij de allrounder; alle cijfers staan hieronder, dus je kunt het zelf nagaan.' + geldZin(r);
    var b = r.bat, w = b.groepW || r.setup.w || 0, two = (r.setups || []).filter(function (s) { return s.units === 2; })[0];
    var kracht = 'De Krachtpatser levert tot ' + wTxt(w) + ' per unit' + (two && two.w > w ? ' (twee units samen ' + wTxt(two.w) + ')' : '') + (w >= P.maxGroepW ? ', per unit de meeste kracht van onze vier.' : '.');
    switch (r.why) {
      case 'wpev': return 'Met een warmtepomp en een elektrische auto vraagt je huis vaak veel vermogen tegelijk. ' + kracht + geldZin(r);
      case 'wp': return 'Je warmtepomp vraagt veel vermogen. ' + kracht + geldZin(r);
      case 'ev': return 'Je elektrische auto vraagt veel vermogen. ' + kracht + geldZin(r);
      case 'airco': return 'Je airco vraagt in de zomer veel vermogen. ' + kracht + geldZin(r);
      case 'app': return r.klein ? 'Klein, licht en mee te nemen als je verhuist: precies wat een appartement vraagt. Je begint met ' + setupKwh(r) + (b.stapKwh ? ' en breidt later uit per ' + stapTxt(b.stapKwh) : '') + '.' + geldZin(r)
        : 'Klein, licht en mee te nemen als je verhuist, en hij dekt wat jij per dag nodig hebt. Precies wat een appartement vraagt.' + geldZin(r);
      case 'klein': return 'Je begint klein, met ' + setupKwh(r) + (b.stapKwh ? ', en breidt later uit per ' + stapTxt(b.stapKwh) : '') + '. Licht en mee te nemen als je verhuist.' + geldZin(r);
      case 'voordelig': return (r.isCheapest ? (r.klein ? 'De laagste instapprijs: ' : 'De laagste aanschafprijs die jouw behoefte dekt: ') : 'Doet gewoon wat hij moet doen, voor een vriendelijke prijs: ') + setupKwh(r) + ' voor ' + eur(r.price) + '.' + geldZin(r);
      case 'rijtjes': return 'Doet gewoon wat hij moet doen, voor een vriendelijke prijs: ' + setupKwh(r) + ' voor ' + eur(r.price) + '. Past goed in een rijtjeshuis.' + geldZin(r);
    }
    if (r.key === a.krachtKey) return (r.why === 'nood' ? 'Voor noodstroom met je grote verbruikers heeft hij ' + ((b.noodW || 0) >= P.maxNoodW ? 'het meeste noodstroomvermogen van onze vier' : 'veel noodstroomvermogen') + ': ' + wTxt(b.noodW || 0) + '. ' : '') + kracht + geldZin(r);
    if (r.key === a.allrounder) {
      if (r.why === 'nood') return 'Noodstroom tot ' + wTxt(b.noodW || 0) + ' en genoeg opslag voor jouw huis. De allrounder: twijfel je, dan is het meestal deze.' + geldZin(r);
      if (r.moneyBest && r.ev.net10High > 0) return 'Levert in jouw huis het meeste op per euro die je erin stopt: ongeveer ' + eur(r.ev.net10High) + ' winst over 10 jaar. De allrounder, dus ook de rest is goed geregeld.';
      return 'De allrounder: past goed bij jouw huis en is uit te breiden als je meer nodig hebt.' + geldZin(r);
    }
    if (r.moneyBest && r.ev.net10High > 0) return 'Levert in jouw huis het meeste op per euro die je erin stopt: ongeveer ' + eur(r.ev.net10High) + ' winst over 10 jaar.';
    return 'Van de vier past deze het best bij jouw huis.' + geldZin(r);
  }
  function minder(r, a, best, P, stopW) {
    var b = r.bat;
    if (r.key === a.meeKey && (P.wp || P.ev)) return 'Met maximaal ' + wTxt(b.groepW || 0) + ' houdt hij een ' + (P.wp ? 'warmtepomp' : 'elektrische auto') + ' niet bij.';
    if (r.key === a.krachtKey && P.app) return 'Meer kracht en gewicht dan je in een appartement nodig hebt.';
    if (r.key === a.krachtKey && P.wens === 'klein') return 'Begint bij ' + setupKwh(r) + ' en is daarmee geen kleine instap.';
    if (r.powerLimited && r.wattEff <= (b.stopW || 800)) return 'Op ' + stopW + ' W haal je ' + kwhTxt(r.setup.kwh) + ' niet elke dag leeg, je betaalt dan voor opslag die je niet gebruikt.';
    if (r.powerLimited) return 'Op ' + r.wattEff.toLocaleString('nl-NL') + ' W haal je ' + kwhTxt(r.setup.kwh) + ' niet elke dag leeg, je betaalt dan voor opslag die je niet gebruikt.';
    if (r.oversize) return 'De kleinste opstelling die jouw behoefte dekt (' + kwhTxt(r.setup.kwh) + ') is ruim groter dan wat jij per dag nodig hebt (ongeveer ' + kwhTxt(r.need) + '). Je betaalt dan voor opslag die je niet gebruikt.';
    if (r.ev.net10High <= 0) return 'Levert in jouw huis minder op dan de beste match en verdient zich langzamer terug.';
    return 'Past wel, maar sluit minder goed aan bij jouw huis en wat jij belangrijk vindt dan ' + best.bat.klasse + '.';
  }
  function uitlegBase(r, label, ctx, a, best, tieHalo, stopW, P) {
    if (label === 'Beste match') return beste(r, ctx, a, tieHalo, P);
    if (r.fail === 'groot') return 'Ook de grootste opstelling (' + kwhTxt((r.reqSetup || r.setup).kwh) + ') dekt je behoefte niet helemaal. Wat er wel kan, bekijken we graag met je via maatwerkadvies.';
    if (r.fail === 'nood') return 'Voor noodstroom met je grote verbruikers is ' + (r.bat.noodW || 0).toLocaleString('nl-NL') + ' W noodstroomvermogen aan de krappe kant.';
    if (label === 'Ook goed') {
      if (r.key === a.allrounder) {
        var groei = r.bat.stapKwh && r.bat.maxKwhUnit ? ' Uit te breiden per ' + stapTxt(r.bat.stapKwh) + ', tot ' + kwhTxt(r.bat.maxKwhUnit) + ' per ' + ((r.bat.stapel && r.bat.stapel.unit) || 'toren') + '.' : '';
        if (r.reqSetup) return 'De allrounder past ook goed bij jouw huis.' + groei;
        return 'De allrounder past ook goed bij jouw huis' + (r.ev.net10High > 0 ? ': ongeveer ' + eur(r.ev.net10High) + ' winst over 10 jaar voor ' + eur(r.price) + '.' : ': ' + setupKwh(r) + ' voor ' + eur(r.price) + '.') + groei;
      }
      if (r.reqSetup) return 'Past ook goed.';
      if (r.ev.net10High > 0) return 'Past ook goed: ongeveer ' + eur(r.ev.net10High) + ' winst over 10 jaar voor ' + eur(r.price) + '.';
      return 'Past ook bij jouw huis.';
    }
    return minder(r, a, best, P, stopW);
  }

  /*
   * Hoofdfunctie. raw: zie prepare(). batteries: [{ key, klasse, model, naam, url, dod, stopW, noodW, pvDc, maxUnits, extra:[..], variants:[{id,title,price,kwh,w,units,modules,image,available}] }]
   * opts: { aannames, only: [keys], forced: { key: setupKey } }
   */
  function compute(raw, batteries, opts) {
    opts = opts || {};
    var a = A(opts);
    var ctx = prepare(raw);
    var forced = opts.forced || {};
    var rs = (batteries || []).filter(function (b) { return !opts.only || opts.only.indexOf(b.key) !== -1; })
      .map(function (b) { return bestFor(ctx, b, a, forced[b.key]); }).filter(Boolean);
    if (!rs.length) return { version: VERSION, results: [], ctx: ctx };
    var need = needPerDay(ctx, a), P = profiel(ctx, need, rs);
    var minPrice = Math.min.apply(null, rs.map(function (r) { return r.price; }));
    rs.forEach(function (r) {
      r.fail = hardFail(ctx, r, a);
      r.isCheapest = r.price === minPrice;
      r.score = r.ev.net10High / r.price;
      if (ctx.raw.wens === 'voordelig' || ctx.raw.wens === 'klein') r.score += a.priceWeight * (minPrice / r.price - 1);
    });
    // Opbrengst per euro als punten: de beste krijgt geldGewicht punten, de rest naar verhouding (minimaal 0)
    var ok = rs.filter(function (r) { return !r.fail; });
    var maxScore = Math.max.apply(null, (ok.length ? ok : rs).map(function (r) { return r.score; }));
    rs.forEach(function (r) {
      var m = maxScore > 0 ? r.score / maxScore : minPrice / r.price;
      m = Math.max(0, Math.min(1, m));
      r.moneyBest = maxScore > 0 && r.score >= maxScore - 1e-9;
      var f = fitFor(r, P, a);
      r.fitPts = f.pts; r.why = f.why;
      r.total = f.pts + a.geldGewicht * m;
    });
    // eerst wat past, dan wat online te koop is (op aanvraag rekent met een kleinere opstelling en mag daardoor niet voordringen)
    rs.sort(function (x, y) { return (x.fail ? 1 : 0) - (y.fail ? 1 : 0) || (x.reqSetup ? 1 : 0) - (y.reqSetup ? 1 : 0) || y.total - x.total; });
    // Vrijwel gelijk: de allrounder wint (bewuste keuze, alle cijfers blijven zichtbaar)
    var tieHalo = '';
    var best = rs[0];
    var halo = rs.filter(function (r) { return r.key === a.allrounder; })[0];
    if (halo && best !== halo && !best.fail && !halo.fail && (best.total - halo.total) <= a.gelijkPunten) {
      tieHalo = best.bat.klasse;
      rs.splice(rs.indexOf(halo), 1);
      rs.unshift(halo);
      best = halo;
    }
    // De allrounder staat altijd bij de eerste twee (besluit Kelvin 6-10-2026)
    if (halo && best !== halo && !halo.fail && rs.indexOf(halo) > 1) {
      rs.splice(rs.indexOf(halo), 1);
      rs.splice(1, 0, halo);
    }
    rs.forEach(function (r, i) {
      var label;
      if (i === 0) label = 'Beste match';
      else if (r.key === a.allrounder && !r.fail) label = 'Ook goed';
      else if (!r.fail && r.fits && r.total >= best.total - a.ookGoedPunten && (r.ev.net10High > 0 || best.ev.net10High <= 0)) label = 'Ook goed';
      else label = 'Minder geschikt';
      r.label = label;
      r.labelKey = { 'Beste match': 'beste', 'Ook goed': 'ookgoed', 'Minder geschikt': 'minder' }[label];
      r.uitleg = uitlegFor(r, label, ctx, a, best, i === 0 ? tieHalo : '', P);
    });
    var bp = best.price;
    return {
      version: VERSION, model: M.VERSION, ctx: ctx, need: need, heavy: powerHeavy(ctx.raw), aannames: a,
      results: rs, best: best, tieHalo: tieHalo,
      prijsklasse: bp < a.grensMidden ? 'tot 2500' : (bp < a.grensHoog ? '2500 tot 3500' : 'boven 3500'),
      adviesNiveau: (bp >= a.grensHoog || best.grote || best.reqSetup) ? 3 : (bp >= a.grensMidden ? 2 : 1),
      groteOpstelling: (bp >= a.grensHoog || best.grote) ? 'ja' : 'nee'
    };
  }

  /* Handige helpers voor de weergave */
  function cartPath(setup) { return '/cart/' + setup.parts.map(function (p) { return p.id + ':' + p.qty; }).join(','); }
  function productUrl(r) { return r.bat.url + '?variant=' + r.setup.variantId; }
  /* Weergave in het gunstige scenario (besluit Kelvin 6-10-2026): één getal, geen bandbreedte */
  function paybackTxt(ev) {
    function y(n) { return n.toFixed(1).replace('.', ','); }
    if (!ev.paybackFast || ev.paybackFast > 20) return 'langer dan de levensduur';
    return 'ongeveer ' + y(ev.paybackFast) + ' jaar';
  }

  var api = { compute: compute, aanvraagSetups: aanvraagSetups, stapelTitle: stapelTitle, setupsFor: function (bat, opts) { return setupsFor(bat, A(opts)); }, cartPath: cartPath,
              productUrl: productUrl, paybackTxt: paybackTxt, eur: eur, kwhTxt: kwhTxt, VERSION: VERSION, DEFAULTS: DEFAULTS };
  if (typeof module !== 'undefined' && module.exports) module.exports = api; else root.SRVierModel = api;
})(typeof window !== 'undefined' ? window : this);
